import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { EmpresaContext } from "../../../contexts/Empresa/EmpresaContext";
import { Button } from "../../../components/Button";
import { Select } from "../../../components/Select";
import Modal from "../../../components/Modal/Modal";
import { ReactComponent as Bloqueado } from "../../../assets/newImages/icons/bloqueado.svg";
import { ReactComponent as Liberado } from "../../../assets/newImages/icons/liberado.svg";
import { ReactComponent as SetaEsquerda } from "../../../assets/newImages/icons/arrow-left.svg";
import { ReactComponent as SetaDireita } from "../../../assets/newImages/icons/arrow-right.svg";
import { ReactComponent as ArrowUp } from "../../../assets/newImages/icons/arrow-up.svg";
import { AssinaturaApi } from "../../../hooks/assinaturaApi";
import {
  ResultadoListarPlanos,
  ResultadoSimulacao,
  TierPlano,
  TierPeriodicidade,
} from "../../../types/Assinatura";
import {
  ViewTrocarPlano,
  Texto,
  Periodicidade,
  ComparativoPermissoes,
  ComparativoPermissoesMobile,
  ScrollToTopButton,
  ViewModalConfirmacao,
} from "./styles";

type PeriodicidadeSel = "mensal" | "anual";

// Troca de plano pelo painel só é liberada no caminho seguro (upgrade de mesma
// periodicidade). Qualquer outro caso cai no atendimento via WhatsApp.
const linkAtendimento = (
  nome: string,
  periodicidadeLabel: string,
  usuario?: string,
  nomeEmpresa?: string
) =>
  `https://api.whatsapp.com/send?phone=555434434563&text=` +
  encodeURIComponent(
    `Olá, gostaria de mudar meu plano para ${nome} (${periodicidadeLabel}). Meu usuário é: ${usuario} da empresa: ${nomeEmpresa}.`
  );

// Centavos → reais formatado (valores da vitrine vêm em centavos).
const reais = (centavos: number) =>
  (centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

// Reais → formatado (valores da simulação já vêm em reais).
const fmtReais = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

// Quota (contagem, não dinheiro) com separador de milhar.
const contagem = (n: number) => n.toLocaleString("pt-BR");

// 'YYYY-MM-DD' → 'DD/MM/YYYY'.
const formatarData = (d?: string) => (d ? d.split("-").reverse().join("/") : "-");

export const TrocarPlano: React.FC = () => {
  const { theme } = useTheme();
  const { empresa } = useContext(EmpresaContext);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<ResultadoListarPlanos | null>(null);
  const [periodicidade, setPeriodicidade] = useState<PeriodicidadeSel>("anual");
  const [tierMobile, setTierMobile] = useState(0);
  const [showScroll, setShowScroll] = useState(false);

  // Fluxo de troca (self-service).
  const [selecionado, setSelecionado] = useState<{ id: number; nome: string } | null>(null);
  const [simulacao, setSimulacao] = useState<ResultadoSimulacao | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [parcelas, setParcelas] = useState(1);
  const [processandoId, setProcessandoId] = useState<number | null>(null);

  useEffect(() => {
    AssinaturaApi()
      .listarPlanosDisponiveis()
      .then((d) => {
        setDados(d);
        setLoading(false);
      })
      .catch((e) => {
        console.error("Erro ao listar planos:", e);
        setErro("Não foi possível carregar os planos. Tente recarregar.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const onScroll = () => setShowScroll(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (loading) {
    return (
      <ViewTrocarPlano theme={theme}>
        <div className="state">Carregando planos...</div>
      </ViewTrocarPlano>
    );
  }

  if (erro || !dados) {
    return (
      <ViewTrocarPlano theme={theme}>
        <div className="state erro">{erro || "Erro ao carregar."}</div>
      </ViewTrocarPlano>
    );
  }

  const { categorias, tiers } = dados;

  if (tiers.length === 0) {
    return (
      <ViewTrocarPlano theme={theme}>
        <div className="state">Nenhum plano disponível no momento.</div>
      </ViewTrocarPlano>
    );
  }

  const meses = periodicidade === "anual" ? 12 : 1;
  const periodicidadeLabel = periodicidade === "anual" ? "Anual" : "Mensal";

  const precoDo = (tier: TierPlano): TierPeriodicidade | null =>
    periodicidade === "anual" ? tier.anual : tier.mensal;

  // Atual só na periodicidade que o cliente realmente tem (o id_plano exato).
  const tierEhAtual = (tier: TierPlano) => {
    const preco = precoDo(tier);
    return !!preco && preco.id_plano === dados.id_plano_atual;
  };

  const maxDescontoAnual = tiers.reduce(
    (max, t) => Math.max(max, t.anual?.desconto_percentual ?? 0),
    0
  );

  // Não é o caminho seguro → direciona direto pro atendimento (nova aba). O
  // simular é rápido, então abre dentro da janela de ativação do navegador.
  const irParaAtendimento = (nome: string) => {
    const link = linkAtendimento(nome, periodicidadeLabel, empresa?.usuario, empresa?.empresa);
    window.open(link, "_blank", "noopener,noreferrer");
  };

  // Clique em "Quero esse plano": simula e só abre o modal se for o caso seguro
  // (upgrade de mesma periodicidade). Resto → atendimento.
  const handleSelecionar = async (idPlano: number, nome: string) => {
    setProcessandoId(idPlano);
    try {
      const sim = await AssinaturaApi().simularMudancaPlano(idPlano);
      if (sim.ok && sim.tipo === "upgrade" && sim.modo === "mesma_periodicidade") {
        setSelecionado({ id: idPlano, nome });
        setSimulacao(sim);
        setParcelas(1);
      } else {
        irParaAtendimento(nome);
      }
    } catch (e) {
      console.error("Erro ao simular mudança:", e);
      irParaAtendimento(nome);
    } finally {
      setProcessandoId(null);
    }
  };

  const handleFecharModal = () => {
    setSelecionado(null);
    setSimulacao(null);
  };

  const handleConfirmar = async () => {
    if (!selecionado) return;
    setConfirmando(true);
    try {
      const res = await AssinaturaApi().trocarPlano(selecionado.id, parcelas);

      if (!res.ok) {
        Swal.fire({
          icon: "error",
          title: "Não foi possível trocar de plano",
          text: res.mensagem || "Tente novamente.",
        });
        return;
      }

      // Upgrade via PIX: cobrança assíncrona — mostra o QR pro cliente pagar.
      if (res.tipo === "upgrade_pix_pendente") {
        const valorFormatado = res.valor_prorata
          ? `R$ ${fmtReais(res.valor_prorata)}`
          : "valor pendente";
        const qrImg = res.qr_code_url
          ? `<img src="${res.qr_code_url}" alt="QR Code PIX" style="max-width:260px;display:block;margin:1rem auto;" />`
          : "";
        const codigo = res.qr_code
          ? `<div style="font-size:11px;word-break:break-all;background:#f5f5f5;padding:0.75rem;border-radius:4px;margin-top:0.5rem;text-align:center;">${res.qr_code}</div>`
          : "";
        await Swal.fire({
          title: "Upgrade aguardando pagamento",
          html:
            `<p>Pague <b>${valorFormatado}</b> via PIX para mudar para <b>${res.plano_alvo_nome ?? "o plano novo"}</b>.</p>` +
            qrImg +
            codigo +
            `<p style="margin-top:1rem;font-size:11px;color:#666;">O upgrade é efetivado automaticamente após o pagamento. Você também pode acessar este PIX na tela de assinatura.</p>`,
          confirmButtonText: "Ir para minha assinatura",
        });
        window.location.href = "/assinatura";
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Plano alterado!",
        text: "Seu novo plano já está ativo.",
      });
      window.location.href = "/assinatura";
    } catch (e: any) {
      console.error("Erro ao trocar plano:", e);
      Swal.fire({
        icon: "error",
        title: "Erro ao processar",
        text: e?.response?.data?.erro || "Tente novamente.",
      });
    } finally {
      setConfirmando(false);
    }
  };

  // Célula de um recurso num tier: ícone (booleano) ou valor/ilimitado (quota).
  const celula = (tier: TierPlano, chave: string, tipo: "booleano" | "quota") => {
    const r = tier.recursos[chave];
    if (!r || r.estado === "bloqueado") return <Bloqueado />;
    if (tipo === "quota") {
      if (r.ilimitado) return <div className="permissao">Ilimitado</div>;
      if (r.valor != null) return <div className="permissao">{contagem(r.valor)}</div>;
    }
    return <Liberado />;
  };

  // Cabeçalho de preço de um tier na periodicidade selecionada.
  const cabecalhoPreco = (tier: TierPlano) => {
    const preco = precoDo(tier);
    const atual = tierEhAtual(tier);

    return (
      <>
        <div className="titulo">
          <div className="nome fonteTitulo">{tier.nome}</div>
          {preco && preco.desconto_percentual > 0 && (
            <div className="desconto fonteRotulos">
              {preco.desconto_percentual}% OFF
            </div>
          )}
        </div>

        <div className="descricao fonteTexto">
          <span>{tier.descricao}</span>
        </div>

        {preco ? (
          <>
            {preco.desconto_percentual > 0 && (
              <div className="valor-riscado fonteRotulos">
                R$ {reais(preco.valor_cheio / meses)}
              </div>
            )}
            <div className="valor fonteTexto">
              R$ <span className="fonteTitulo">{reais(preco.valor_mensal_equivalente)}</span>
              /ao mês
            </div>
            <div className="equivalente fonteRotulos">
              {periodicidade === "anual"
                ? `Total ano: R$ ${reais(
                    preco.valor_com_desconto ?? preco.valor_cheio
                  )} em até ${preco.parcelas}x`
                : `Equivalente a R$ ${reais(
                    preco.valor_mensal_equivalente / 30
                  )}/dia`}
            </div>

            {!atual && (
              <Button
                title="Quero esse plano"
                width="100%"
                loading={processandoId === preco.id_plano}
                onClick={() => handleSelecionar(preco.id_plano, tier.nome)}
              />
            )}
          </>
        ) : (
          <div className="equivalente fonteRotulos">
            Indisponível nesta periodicidade.
          </div>
        )}
      </>
    );
  };

  const tierAtualMobile = tiers[tierMobile];
  const proxTier = () => setTierMobile((i) => (i + 1) % tiers.length);
  const antTier = () => setTierMobile((i) => (i - 1 + tiers.length) % tiers.length);

  return (
    <ViewTrocarPlano theme={theme}>
      <Texto theme={theme}>
        Quer migrar entre os planos? Escolha abaixo a periodicidade desejada e
        selecione seu plano.
      </Texto>

      <Periodicidade theme={theme}>
        {(["mensal", "anual"] as PeriodicidadeSel[]).map((p) => (
          <div
            key={p}
            className={`fonteTexto opcao ${p === periodicidade ? "selecionado" : ""}`}
            onClick={() => setPeriodicidade(p)}
          >
            {p === "anual" ? "Anual" : "Mensal"}
            {p === "anual" && maxDescontoAnual > 0 && (
              <span className="fonteRotulos"> Até {maxDescontoAnual}% OFF</span>
            )}
          </div>
        ))}
      </Periodicidade>

      {/* Comparativo — desktop */}
      <ComparativoPermissoes theme={theme}>
        <table className="hideMobile">
          <thead>
            <tr className="cabecalho-planos">
              <th className="recursos fonteTitulo">Recursos</th>
              {tiers.map((tier) => (
                <th
                  key={tier.nome}
                  className={tierEhAtual(tier) ? "plano-atual-col" : ""}
                >
                  <div className={tierEhAtual(tier) ? "plano-atual" : ""}>
                    {cabecalhoPreco(tier)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categorias.map((cat) => (
              <React.Fragment key={cat.id}>
                <tr className="secao fonteSubtitulo">
                  <td colSpan={tiers.length + 1}>{cat.nome}</td>
                </tr>
                {cat.recursos.map((rec) => (
                  <tr key={rec.chave}>
                    <td className="recurso fonteTexto">{rec.descricao}</td>
                    {tiers.map((tier) => (
                      <td
                        key={tier.nome}
                        className={`informacao fonteTexto ${
                          tierEhAtual(tier) ? "plano-atual-cell" : ""
                        }`}
                      >
                        {celula(tier, rec.chave, rec.tipo)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </ComparativoPermissoes>

      {/* Comparativo — mobile (carousel por tier) */}
      <ComparativoPermissoesMobile theme={theme}>
        <div className="plano-atual hideDesktop">
          <div className="cabecalho">
            <button onClick={antTier} className="seta-esquerda">
              <SetaEsquerda />
            </button>
            <div
              className={`informacoes-plano ${
                tierEhAtual(tierAtualMobile) ? "seu-plano-atual" : ""
              }`}
            >
              {cabecalhoPreco(tierAtualMobile)}
            </div>
            <button onClick={proxTier} className="seta-direita">
              <SetaDireita />
            </button>
          </div>

          {categorias.map((cat) => (
            <div
              key={cat.id}
              className={`secao-permissoes ${
                tierEhAtual(tierAtualMobile) ? "seu-plano-atual" : ""
              }`}
            >
              <div className="titulo-secao">{cat.nome}</div>
              {cat.recursos.map((rec) => (
                <div key={rec.chave} className="lista-permissoes">
                  <div className="titulo">{rec.descricao}</div>
                  <div className="informacao">
                    {celula(tierAtualMobile, rec.chave, rec.tipo)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </ComparativoPermissoesMobile>

      {showScroll && (
        <ScrollToTopButton
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          theme={theme}
        >
          <ArrowUp />
        </ScrollToTopButton>
      )}

      {/* Modal de confirmação — só para upgrade de mesma periodicidade */}
      <Modal
        isOpen={!!selecionado}
        setModal={(v: boolean) => {
          if (!v) handleFecharModal();
        }}
        width={"55rem"}
        align="center"
        justify="center"
        animation="opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)"
        margin="0rem 2rem"
        padding={"3rem"}
      >
        <ViewModalConfirmacao theme={theme}>
          <div className="plano-alvo fonteTexto">
            Novo plano: <strong>{selecionado?.nome}</strong>
          </div>

          {simulacao && (
            <div className="preview fonteTexto">
              <p>
                {simulacao.forma_pagamento === "pix" ? (
                  <>
                    Geraremos um PIX de{" "}
                    <strong>R$ {fmtReais(simulacao.valor_a_cobrar || 0)}</strong>{" "}
                    referente aos dias restantes no plano novo (já descontado o
                    que você pagou no plano atual). O upgrade é efetivado após a
                    confirmação do pagamento.
                  </>
                ) : (
                  <>
                    Você pagará no cartão{" "}
                    <strong>R$ {fmtReais(simulacao.valor_a_cobrar || 0)}</strong>{" "}
                    referente aos dias restantes no plano novo, já descontado o
                    que você pagou no plano atual.
                  </>
                )}
              </p>
              <small>
                Novo período: {formatarData(simulacao.data_inicio)} até{" "}
                {formatarData(simulacao.data_fim)}.
              </small>
            </div>
          )}

          {simulacao &&
            simulacao.forma_pagamento === "cartao" &&
            (simulacao.parcelas_max ?? 1) > 1 && (
              <div style={{ marginTop: "1.2rem" }}>
                <Select
                  label="Parcelas"
                  value={parcelas}
                  onChange={(v) => setParcelas(Number(v))}
                  options={Array.from(
                    { length: simulacao.parcelas_max ?? 1 },
                    (_, i) => i + 1
                  ).map((n) => ({
                    name: `${n}x de R$ ${fmtReais(
                      (simulacao.valor_a_cobrar || 0) / n
                    )}`,
                    value: n,
                  }))}
                />
              </div>
            )}

          <div className="acoes">
            <Button
              title="Voltar"
              className="fonteTexto botao-secundario"
              onClick={handleFecharModal}
              width="auto"
              padding="0 2rem"
            />
            <Button
              title={confirmando ? "Processando..." : "Confirmar"}
              className="fonteTexto"
              onClick={handleConfirmar}
              width="auto"
              padding="0 2rem"
              loading={confirmando}
            />
          </div>
        </ViewModalConfirmacao>
      </Modal>
    </ViewTrocarPlano>
  );
};

export default TrocarPlano;
